import { useEffect, useState } from "react";
import { useImpersonation, loadImpersonationOrigin } from "@/hooks/useImpersonation";
import { useUser } from "@/hooks/useUser";
import { useLocation } from "wouter";
import { endImpersonation } from "@/lib/admin-api";
import { Sentry } from "@/lib/sentry";
import { Eye, X } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

const IMPERSONATION_START_KEY = "bac_impersonation_started_at";

function formatElapsed(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export function ImpersonationBanner() {
  const { impersonating, stopImpersonation, isImpersonating } = useImpersonation();
  const { session } = useUser();
  const [, setLocation] = useLocation();
  const [now, setNow] = useState(() => Date.now());
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isImpersonating) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isImpersonating]);

  useEffect(() => {
    if (!isImpersonating || !impersonating) return;
    if (!sessionStorage.getItem(IMPERSONATION_START_KEY)) {
      sessionStorage.setItem(IMPERSONATION_START_KEY, String(Date.now()));
    }
  }, [isImpersonating, impersonating]);

  if (!isImpersonating || !impersonating) return null;

  const startedAtRaw = Number(sessionStorage.getItem(IMPERSONATION_START_KEY) || "0");
  const startedAt = startedAtRaw || (impersonating.expiresAt - 60 * 60 * 1000);
  const elapsedMs = Math.max(0, now - startedAt);
  const remainingMs = Math.max(0, impersonating.expiresAt - now);

  const handleExit = async () => {
    if (busy) return;
    setBusy(true);
    if (session?.access_token) {
      try {
        await endImpersonation(session.access_token, impersonating.sessionId, impersonating.userId);
      } catch (err) {
        console.error("[ImpersonationBanner] Failed to end impersonation:", err);
        Sentry.captureException(err);
      }
    }
    sessionStorage.removeItem(IMPERSONATION_START_KEY);
    // Task #6273 — return the admin to where they started impersonating from
    // (path + query), falling back to /admin/users when no origin was
    // recorded. Read before stopImpersonation(), which clears the stored
    // origin along with the rest of the impersonation state.
    const origin = loadImpersonationOrigin();
    stopImpersonation();
    setLocation(origin ?? "/admin/users");
  };

  return (
    <div className="sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-white shadow-md">
      <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        <Trans
          i18nKey="impersonation.viewing_as"
          values={{ name: impersonating.name, email: impersonating.email }}
          components={{ name: <strong /> }}
        />
      </span>
      <span className="inline-flex items-center gap-2 rounded-full bg-background/15 px-2.5 py-0.5 text-xs tabular-nums" aria-live="polite">
        <span title={t("impersonation.elapsed_title")}>⏱ {formatElapsed(elapsedMs)}</span>
        <span className="opacity-70">·</span>
        <span title={t("impersonation.remaining_title")}>
          {t("impersonation.minutes_left", { count: Math.ceil(remainingMs / 60000) })}
        </span>
      </span>
      <button
        onClick={handleExit}
        disabled={busy}
        className="cursor-pointer ml-1 inline-flex items-center gap-1 rounded-full bg-background/20 px-3 py-1 text-xs font-semibold hover:bg-background/30 transition-colors disabled:opacity-60"
        title={t("impersonation.end_session_title")}
      >
        <X className="h-3 w-3" /> {busy ? t("impersonation.ending") : t("impersonation.end_session")}
      </button>
    </div>
  );
}
